import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@/redux/store';
import { ModalSlice } from '@/redux/modal';
import { buildOvmClickoutUrl, fetchIustaOvmLink } from '@/utils/iusta-ovm-link';
import './ovm-link-pending.css';

const POLL_INTERVAL_MS = 5000;
const COUNTDOWN_SECONDS = 60;

/**
 * Shown when the long-read CTA is clicked but IUSTA hasn't generated the personal OVM link
 * (custom field 24448) yet. Polls every 5s and redirects automatically the moment the link is
 * ready — no user interaction required. After the initial 60s countdown elapses without success,
 * swaps in a "this is taking longer than usual" message, but keeps polling silently in the
 * background so a late-arriving link still triggers the redirect.
 */
const OvmLinkPendingMain = (props) => {
  const { leadId, appendPoaToCTA, answeredSlides, onUpdateModifiers } = props;
  const dispatch = useAppDispatch();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [timedOut, setTimedOut] = useState(false);
  const redirectedRef = useRef(false);

  useEffect(() => {
    // 2026-08-17: use the same 'default' modifier as the other simple text/message modals
    // (e.g. FilesRejectionMain) so this picks up the shared white modal__body background +
    // padding from modal.css (.modal__body--default) instead of rendering unstyled.
    onUpdateModifiers && onUpdateModifiers({ add: ['default'] });
  }, []);

  useEffect(() => {
    const countdownId = setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          setTimedOut(true);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    const pollId = setInterval(async () => {
      if (redirectedRef.current) return;
      const result = await fetchIustaOvmLink(leadId);
      if (result.ready && result.url && !redirectedRef.current) {
        redirectedRef.current = true;
        clearInterval(countdownId);
        clearInterval(pollId);
        dispatch(ModalSlice.actions.closeModal());
        window.location.href = buildOvmClickoutUrl(result.url, appendPoaToCTA, answeredSlides);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(countdownId);
      clearInterval(pollId);
    };
  }, [leadId]);

  return (
    <div className='ovm-link-pending'>
      <h1 className='ovm-link-pending__title'>Ihre Online-Vollmacht wird vorbereitet</h1>
      {!timedOut ? (
        <>
          <p className='ovm-link-pending__text'>Ihr persönlicher Link ist in wenigen Sekunden bereit.</p>
          <p className='ovm-link-pending__countdown'>Noch ca. {secondsLeft} Sekunden</p>
        </>
      ) : (
        <p className='ovm-link-pending__text'>
          Heute dauert es leider etwas länger. Bitte warten Sie noch einen Moment oder schauen Sie in Ihren E-Mails
          nach. Sobald Ihre Online-Vollmacht erstellt wurde, senden wir Ihnen zusätzlich eine Bestätigungs-E-Mail mit
          dem Link zu.
        </p>
      )}
    </div>
  );
};

OvmLinkPendingMain.displayName = 'OvmLinkPendingMain';

export default OvmLinkPendingMain;
